import {
  Box,
  Button,
  Chip,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Option,
  Select,
  Sheet,
  Table,
  Typography,
} from '@mui/joy';
import { ArrowDownIcon, ArrowUpIcon } from '@heroicons/react/outline';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import LockResetRoundedIcon from '@mui/icons-material/LockResetRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useUser } from '../../hooks/useUser';
import useUsers from '../../hooks/useUsers';
import useBranches from '../../hooks/usebranches';
import { getAllTheUsers } from '../../redux/actions/userActions';
import AdminResetModal from '../../features/mfa/admin/components/AdminResetModal';
import Modal from './modal';
import { User } from './types';

type SortConfig = {
  key: keyof User | 'status' | 'personType' | 'email';
  direction: 'asc' | 'desc';
};

// Percentages, NOT pixels. With table-layout:fixed a px column width is
// honoured literally and the table grows past its container instead of
// shrinking to it — which is how the last column ("Actie", holding the only
// "Reset 2FA" entry point) ended up clipped off-screen behind an
// overflow:hidden. Percentages are true ratios of the panel width, so the
// table always fits whatever space the layout gives it.
//
// Keep the total at 100.
const COLUMN_WIDTHS = {
  firstName: '12%',
  email: '22%',
  branch: '12%',
  role: '12%',
  personType: '10%',
  status: '10%',
  mfa: '7%',
  action: '15%',
} as const;

const UserList = () => {
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState<User | null>(null);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  // 25 rather than 10: with the panel now at full grid width and full viewport
  // height, 10 rows left most of the card empty and forced needless paging.
  const [rowsPerPage] = useState(25);
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);

  const dispatch = useDispatch();
  const usersQuery = useUsers();
  const branchesQuery = useBranches();
  const userQuery = useUser();

  useEffect(() => {
    dispatch(getAllTheUsers());
  }, [dispatch]);

  useEffect(() => {
    if (!modalOpen) {
      setSelectedUser(null);
      dispatch(getAllTheUsers());
    }
  }, [dispatch, modalOpen]);

  useEffect(() => {
    if (selectedBranch.length) return;
    if (!branchesQuery.isSuccess) return;
    if (!userQuery.isSuccess) return;

    setSelectedBranch(userQuery.data.branch);
  }, [branchesQuery, userQuery]);

  const handleSort = (key: SortConfig['key']) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === 'asc'
    ) {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const filteredAndSortedRows = useMemo(() => {
    if (!usersQuery.isSuccess) return [];
    if (!branchesQuery.isSuccess) return [];

    let users = usersQuery.data;

    // Filter by branch
    if (selectedBranch && selectedBranch !== 'all') {
      users = users.filter(
        (user) => user.branch.toLowerCase() === selectedBranch.toLowerCase()
      );
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      users = users.filter(
        (user) =>
          user.firstName?.toLowerCase().includes(query) ||
          user.lastName?.toLowerCase().includes(query) ||
          user.email?.toLowerCase().includes(query) ||
          user.branch?.toLowerCase().includes(query) ||
          user.role?.toLowerCase().includes(query) ||
          user.status?.toLowerCase().includes(query) ||
          user.personType?.toLowerCase().includes(query)
      );
    }

    // Sort
    if (sortConfig) {
      users = [...users].sort((a, b) => {
        const aValue = a[sortConfig.key as keyof typeof a] || '';
        const bValue = b[sortConfig.key as keyof typeof b] || '';

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return users;
  }, [usersQuery, branchesQuery, selectedBranch, searchQuery, sortConfig]);

  // Pagination
  const paginatedRows = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredAndSortedRows.slice(startIndex, endIndex);
  }, [filteredAndSortedRows, currentPage, rowsPerPage]);

  const totalPages = Math.ceil(filteredAndSortedRows.length / rowsPerPage);

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'ADMIN':
        return 'warning';
      case 'USER':
        return 'primary';
      case 'SUPER_ADMIN':
        return 'danger';
      default:
        return 'neutral';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'danger';
      default:
        return 'neutral';
    }
  };

  if (!usersQuery.isSuccess) return null;
  if (!branchesQuery.isSuccess) return null;
  if (!userQuery.isSuccess) return null;

  let branchOptions: string[] = [userQuery.data.branch];
  if (userQuery.data.role === 'SUPER_ADMIN') {
    branchOptions = [
      'all',
      ...branchesQuery.data.map((branch: any) => branch.name),
    ];
  }

  const SortIcon = ({ column }: { column: SortConfig['key'] }) => {
    if (!sortConfig || sortConfig.key !== column) {
      return null;
    }
    return sortConfig.direction === 'asc' ? (
      <ArrowUpIcon className="w-4 h-4 ml-1" />
    ) : (
      <ArrowDownIcon className="w-4 h-4 ml-1" />
    );
  };

  return (
    <>
      {selectedUser && (
        <Modal
          isOpen={modalOpen}
          setOpen={setModalOpen}
          user={selectedUser}
          refetch={usersQuery.refetch}
        />
      )}

      {resetTarget && (
        <AdminResetModal
          isOpen={Boolean(resetTarget)}
          onClose={() => setResetTarget(null)}
          onSuccess={() => {
            setResetTarget(null);
            usersQuery.refetch();
          }}
          targetUserId={resetTarget._id}
          targetUserEmail={(resetTarget as any).email ?? resetTarget._id}
        />
      )}

      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <FormControl sx={{ minWidth: 200 }}>
          <FormLabel>Vestiging</FormLabel>
          <Select
            value={selectedBranch}
            onChange={(_, value) => setSelectedBranch(value as string)}
            disabled={userQuery.data.role !== 'SUPER_ADMIN'}
          >
            {branchOptions.map((branch) => (
              <Option key={branch} value={branch}>
                {branch === 'all' ? 'Alle vestigingen' : branch}
              </Option>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ flex: 1, maxWidth: 400 }}>
          <FormLabel>Zoeken</FormLabel>
          <Input
            placeholder="Zoek op naam, email, rol, status..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            startDecorator={<SearchRoundedIcon />}
          />
        </FormControl>
      </Box>

      <Sheet
        variant="outlined"
        sx={{
          borderRadius: 'sm',
          overflowX: 'auto',
          overflowY: 'auto',
          // Stretch to the bottom of the available space instead of hugging a
          // handful of rows. minHeight keeps it sane if the viewport is short.
          flex: 1,
          minHeight: 240,
        }}
      >
        {/*
          width:100% + tableLayout:fixed + percentage column widths (see
          COLUMN_WIDTHS) make the columns scale proportionally to exactly fill
          the panel. Long emails use overflowWrap so they wrap in-cell.

          overflowX is 'auto', not 'hidden': at desktop widths the percentages
          mean no scrollbar ever appears, but if the panel is ever squeezed
          (small laptop, future layout change) the user gets a scrollbar
          instead of silently losing the Action column and the controls in it.
        */}
        <Table stickyHeader sx={{ width: '100%', tableLayout: 'fixed' }}>
          <thead>
            <tr>
              <th style={{ width: COLUMN_WIDTHS.firstName }}>
                <Button
                  variant="plain"
                  color="neutral"
                  size="sm"
                  onClick={() => handleSort('firstName')}
                  sx={{ fontWeight: 'bold', justifyContent: 'flex-start' }}
                >
                  Voornaam
                  <SortIcon column="firstName" />
                </Button>
              </th>
              <th style={{ width: COLUMN_WIDTHS.email }}>
                <Button
                  variant="plain"
                  color="neutral"
                  size="sm"
                  onClick={() => handleSort('email')}
                  sx={{ fontWeight: 'bold', justifyContent: 'flex-start' }}
                >
                  Email
                  <SortIcon column="email" />
                </Button>
              </th>
              <th style={{ width: COLUMN_WIDTHS.branch }}>
                <Button
                  variant="plain"
                  color="neutral"
                  size="sm"
                  onClick={() => handleSort('branch')}
                  sx={{ fontWeight: 'bold', justifyContent: 'flex-start' }}
                >
                  Vestiging
                  <SortIcon column="branch" />
                </Button>
              </th>
              <th style={{ width: COLUMN_WIDTHS.role }}>
                <Button
                  variant="plain"
                  color="neutral"
                  size="sm"
                  onClick={() => handleSort('role')}
                  sx={{ fontWeight: 'bold', justifyContent: 'flex-start' }}
                >
                  Rol
                  <SortIcon column="role" />
                </Button>
              </th>
              <th style={{ width: COLUMN_WIDTHS.personType }}>
                <Button
                  variant="plain"
                  color="neutral"
                  size="sm"
                  onClick={() => handleSort('personType')}
                  sx={{ fontWeight: 'bold', justifyContent: 'flex-start' }}
                >
                  Type
                  <SortIcon column="personType" />
                </Button>
              </th>
              <th style={{ width: COLUMN_WIDTHS.status }}>
                <Button
                  variant="plain"
                  color="neutral"
                  size="sm"
                  onClick={() => handleSort('status')}
                  sx={{ fontWeight: 'bold', justifyContent: 'flex-start' }}
                >
                  Status
                  <SortIcon column="status" />
                </Button>
              </th>
              <th style={{ width: COLUMN_WIDTHS.mfa, whiteSpace: 'nowrap' }}>2FA</th>
              <th style={{ width: COLUMN_WIDTHS.action, whiteSpace: 'nowrap' }}>Actie</th>
            </tr>
          </thead>
          <tbody>
            {paginatedRows.map((user) => (
              <tr key={user._id}>
                <td style={{ overflowWrap: 'anywhere' }}>{user.firstName}</td>
                <td style={{ overflowWrap: 'anywhere' }}>{user.email}</td>
                <td style={{ overflowWrap: 'anywhere' }}>{user.branch}</td>
                <td>
                  <Chip
                    size="sm"
                    variant="soft"
                    color={getRoleColor(user.role)}
                  >
                    {user.role}
                  </Chip>
                </td>
                <td style={{ overflowWrap: 'anywhere' }}>
                  {user.personType || '-'}
                </td>
                <td>
                  <Chip
                    size="sm"
                    variant="soft"
                    color={getStatusColor(user.status)}
                  >
                    {user.status || 'unknown'}
                  </Chip>
                </td>
                <td>
                  <Chip
                    size="sm"
                    variant="soft"
                    color={user.mfaEnabled ? 'success' : 'neutral'}
                  >
                    {user.mfaEnabled ? 'Aan' : 'Uit'}
                  </Chip>
                </td>
                <td>
                  <IconButton
                    size="sm"
                    variant="plain"
                    color="neutral"
                    onClick={() => {
                      setSelectedUser(user as any);
                      setModalOpen(true);
                    }}
                  >
                    <EditRoundedIcon />
                  </IconButton>
                  <IconButton
                    size="sm"
                    variant="plain"
                    color="warning"
                    title="2FA resetten"
                    disabled={!user.mfaEnabled}
                    onClick={() => setResetTarget(user as any)}
                  >
                    <LockResetRoundedIcon />
                  </IconButton>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Sheet>

      {totalPages > 1 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 2,
          }}
        >
          <Typography level="body-sm">
            Toont {(currentPage - 1) * rowsPerPage + 1} -{' '}
            {Math.min(currentPage * rowsPerPage, filteredAndSortedRows.length)}{' '}
            van {filteredAndSortedRows.length} gebruikers
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="sm"
              variant="outlined"
              color="neutral"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(currentPage - 1)}
            >
              Vorige
            </Button>
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <Button
                    key={i}
                    size="sm"
                    variant={currentPage === pageNum ? 'solid' : 'plain'}
                    color={currentPage === pageNum ? 'primary' : 'neutral'}
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </Button>
                );
              })}
            </Box>
            <Button
              size="sm"
              variant="outlined"
              color="neutral"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              Volgende
            </Button>
          </Box>
        </Box>
      )}
    </>
  );
};

export default UserList;
